import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { STORAGE_KEYS } from '@shared/constants';

export type ThemeMode = 'light' | 'dark';
export type ThemePreference = 'light' | 'dark' | 'system';

interface ThemeState {
  /** What the user picked — 'system' follows the OS setting live. */
  preference: ThemePreference;
  /** The resolved 'light'/'dark' actually applied to the document. */
  mode: ThemeMode;
  themeSlug: string | null;
  setPreference: (preference: ThemePreference) => void;
  setTheme: (slug: string, mode: ThemeMode) => void;
  toggle: () => void;
}

const resolveSystemMode = (): ThemeMode =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';

const resolveMode = (preference: ThemePreference): ThemeMode =>
  preference === 'system' ? resolveSystemMode() : preference;

const applyModeToDocument = (mode: ThemeMode): void => {
  document.documentElement.classList.toggle('dark', mode === 'dark');
  document.documentElement.setAttribute('data-theme', mode);
};

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      preference: 'system',
      mode: 'dark',
      themeSlug: null,
      setPreference: (preference) => {
        const mode = resolveMode(preference);
        applyModeToDocument(mode);
        set({ preference, mode, themeSlug: null });
      },
      setTheme: (slug, mode) => {
        applyModeToDocument(mode);
        set({ themeSlug: slug, mode, preference: mode });
      },
      toggle: () => {
        const next = get().mode === 'dark' ? 'light' : 'dark';
        applyModeToDocument(next);
        set({ mode: next, preference: next, themeSlug: null });
      },
    }),
    {
      name: STORAGE_KEYS.themePreference,
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const mode = resolveMode(state.preference);
        state.mode = mode;
        applyModeToDocument(mode);
      },
    },
  ),
);

// Keeps the resolved mode in sync live while following the OS setting.
if (typeof window !== 'undefined') {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (event) => {
    if (useThemeStore.getState().preference !== 'system') return;
    const mode: ThemeMode = event.matches ? 'dark' : 'light';
    applyModeToDocument(mode);
    useThemeStore.setState({ mode });
  });
}
